import {
  JobSafetyAnalysisType,
  type JobSafetyAnalysisFilter,
  type JsaReviewCandidatesQuery,
  type JsaReviewCandidatesQueryVariables,
} from '__generated__/graphql';
import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { compact, flatten, map } from 'lodash';
import type ICostCode from 'types/CostCode';
import type IProject from 'types/Project';
import type { NonNullArrayField } from 'types/util/NonNullArrayField';
import type { Nullable } from 'types/util/Nullable';

const JSA_REVIEW_CANDIDATES = gql`
  query JsaReviewCandidates($filter: JobSafetyAnalysisFilter) {
    jobSafetyAnalyses(filter: $filter) {
      id
      type
      frequency
      scopeToProject
      clockInReviewEnabled
      createdOn
      updatedOn
      project {
        id
        title
        depth
      }
    }
  }
`;

export type TJsaReviewCandidatesData = JsaReviewCandidatesQuery['jobSafetyAnalyses'];
export type TJsaReviewCandidate = NonNullArrayField<TJsaReviewCandidatesData>;
export type UseRequestJsaCandidates = () => (
  costCodeId?: Nullable<ICostCode['id']>,
  projectId?: Nullable<IProject['id']>
) => Promise<TJsaReviewCandidatesData>;
export const useRequestJsaCandidates: UseRequestJsaCandidates = () => {
  const requestCostCodeJsaCandidates = useRequestCostCodeJsaCandidates();
  const requestTaskJsaCandidates = useRequestTaskJsaCandidates();

  return (costCodeId, projectId) =>
    Promise.all([requestCostCodeJsaCandidates(costCodeId, projectId), requestTaskJsaCandidates(projectId)]).then(
      (results) => {
        const jobSafetyAnalyses = compact(flatten(results));
        return jobSafetyAnalyses;
      }
    );
};

export type UseRequestCostCodeJsaCandidates = () => (
  costCodeId?: Nullable<ICostCode['id']>,
  projectId?: Nullable<IProject['id']>
) => Promise<TJsaReviewCandidatesData>;
export const useRequestCostCodeJsaCandidates: UseRequestCostCodeJsaCandidates = () => {
  const client = useGraphQLClient();
  return (costCodeId, projectId) => {
    if (!costCodeId) {
      return Promise.resolve([]);
    }

    const baseFilter: JobSafetyAnalysisFilter = {
      type: { equal: JobSafetyAnalysisType.CostCode },
      costCodeId: { equal: costCodeId },
      clockInReviewEnabled: { equal: true },
      finalizedOn: { isNull: false },
      archivedOn: { isNull: true },
      deletedOn: { isNull: true },
    };

    // always request JSA's not scoped to a project
    const requests = [
      () => {
        const unscopedFilter: JobSafetyAnalysisFilter = {
          ...baseFilter,
          scopeToProject: { equalOrNull: false },
        };

        return client.request<JsaReviewCandidatesQuery, JsaReviewCandidatesQueryVariables>({
          document: JSA_REVIEW_CANDIDATES,
          variables: { filter: unscopedFilter },
        });
      },
    ];

    if (projectId) {
      // get JSA's' scoped to this project or any ancestor project
      requests.push(() => {
        const scopedFilter: JobSafetyAnalysisFilter = {
          ...baseFilter,
          scopeToProject: { equal: true },
          project: { idWithAncestors: { equal: projectId } },
        };

        return client.request<JsaReviewCandidatesQuery, JsaReviewCandidatesQueryVariables>({
          document: JSA_REVIEW_CANDIDATES,
          variables: { filter: scopedFilter },
        });
      });
    }

    return Promise.all(requests.map((fn) => fn())).then((results) => {
      const jobSafetyAnalyses = compact(flatten(map(results, 'jobSafetyAnalyses')));
      return jobSafetyAnalyses;
    });
  };
};

export type UseRequestTaskJsaCandidates = () => (
  projectId?: Nullable<IProject['id']>
) => Promise<TJsaReviewCandidatesData>;
export const useRequestTaskJsaCandidates: UseRequestTaskJsaCandidates = () => {
  const client = useGraphQLClient();
  return (projectId) => {
    if (!projectId) {
      return Promise.resolve([]);
    }

    const filter: JobSafetyAnalysisFilter = {
      type: { equal: JobSafetyAnalysisType.Task },
      project: { idWithAncestors: { equal: projectId } },
      clockInReviewEnabled: { equal: true },
      finalizedOn: { isNull: false },
      archivedOn: { isNull: true },
      deletedOn: { isNull: true },
    };

    return client
      .request<JsaReviewCandidatesQuery, JsaReviewCandidatesQueryVariables>({
        document: JSA_REVIEW_CANDIDATES,
        variables: { filter },
      })
      .then((results) => {
        const jobSafetyAnalyses = compact(flatten(results.jobSafetyAnalyses));
        return jobSafetyAnalyses;
      });
  };
};
